/*
 * Copyright (c) 2020. Dave van Rijn Development
 */

import {ModelResponse, ResponseData} from '@dvrd/dvr-controls';
import {UserStatus, UserType} from '../util/interfaces';
import {getUser} from '../util/requestSender';
import {BaseModel, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';

interface Props {
    id?: string;
    created_at?: string;
    updated_at?: string;
    last_login?: string;
    email?: string;
    firstname?: string;
    infix?: string;
    lastname?: string;
    status?: UserStatus;
    flex?: Record<string, any>;
    type?: UserType;
    two_factor_enabled?: number | boolean;
}

interface Fields {
    id: string;
    created_at: string;
    updated_at: string;
    last_login: string;
    email: string;
    firstname: string;
    infix: string;
    lastname: string;
    status: UserStatus;
    flex: Record<string, any>;
    type: UserType;
    two_factor_enabled: boolean;
}

const fields: Fields = {
    id: '',
    created_at: '',
    updated_at: '',
    last_login: '',
    email: '',
    firstname: '',
    infix: '',
    lastname: '',
    status: UserStatus.ACTIVE,
    flex: {},
    type: UserType.USER,
    two_factor_enabled: false
};

const fieldsDefinition: { [key in keyof Fields]: FieldDefinition } = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    last_login: createFieldDef(ModelFieldType.STRING, undefined, true),
    email: createFieldDef(ModelFieldType.STRING),
    firstname: createFieldDef(ModelFieldType.STRING),
    infix: createFieldDef(ModelFieldType.STRING),
    lastname: createFieldDef(ModelFieldType.STRING),
    status: createFieldDef(ModelFieldType.STRING, UserStatus.ACTIVE),
    flex: createFieldDef(ModelFieldType.OBJECT),
    type: createFieldDef(ModelFieldType.STRING),
    two_factor_enabled: createFieldDef(ModelFieldType.NUMBER, false, false, false, true)
};

const skipFields: string[] = [];

export default class User extends BaseModel<Props, Fields> {
    static get = (callback: ModelResponse<User | null>): void => {
        getUser({
            callback: (data: ResponseData) => {
                const success = data.success, user = success ? new User(data.user) : null;
                callback(user, success, data);
            }
        });
    };

    constructor(props?: Props) {
        super(skipFields, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id;
    }

    set id(id: string) {
        this.fields.id = id;
    }

    get email(): string {
        return this.fields.email;
    }

    set email(email: string) {
        this.fields.email = email;
    }

    get firstname(): string {
        return this.fields.firstname;
    }

    get infix(): string {
        return this.fields.infix;
    }

    get lastname(): string {
        return this.fields.lastname;
    }

    get fullName(): string {
        let name = this.firstname;
        if (this.infix.length) name += ` ${this.infix}`;
        if (this.lastname.length) name += ` ${this.lastname}`;
        return name;
    }

    get type(): UserType {
        return this.fields.type;
    }

    set type(type: UserType) {
        this.fields.type = type;
    }

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
        }
    };
}